import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customStyles } from '../../Utils/utils';
import GpHeader from './GpHeader';
import {
  gpGetReport,
  gpGetReportPdfBlobUrl,
  gpGetReportLiteImages,
  gpGetReportMeasurements,
  GpReportDetail,
  GpLiteInstance,
  GpMeasurement,
} from '../../rtk-apis/gp/gpApi';
import GpMeasurements from './GpMeasurements';
import '../../styled/pages/dashboard.scss';

const typeLabel = (t?: string) => (t === 'stressEcho' ? 'Stress Echo' : 'Echo (TTE)');

const isVideo = (url?: string) => /\.(mp4|webm|mov)(\?|$)/i.test(url || '');

/** "20260612 152057" / "20260612" → "12/06/2026 15:20" */
const formatStudyDateTime = (date?: string, time?: string): string => {
  if (!date) return '—';
  const raw = String(date).trim();
  let formatted = raw;
  const m = raw.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) formatted = `${m[3]}/${m[2]}/${m[1]}`;
  const t = String(time || '').trim();
  const tm = t.match(/^(\d{2})(\d{2})(\d{2})?/);
  if (tm) formatted += ` · ${tm[1]}:${tm[2]}`;
  return formatted;
};

/** Hide raw ObjectIds / "NA" coming back from the backend */
const displayable = (v?: string): string | null => {
  const s = String(v || '').trim();
  if (!s || s === 'NA' || s === '—') return null;
  if (/^[a-f0-9]{24,}$/i.test(s)) return null;
  return s;
};

const captionFor = (inst: GpLiteInstance): string | null => {
  const parts = [displayable(inst.stage), displayable(inst.view)].filter(Boolean);
  return parts.length ? parts.join(' — ') : null;
};

const infoLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.4)',
  marginBottom: '3px',
};

const infoValueStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#fff',
  lineHeight: 1.4,
};

const mediaStyle: React.CSSProperties = {
  width: '100%',
  display: 'block',
  aspectRatio: '4 / 3',
  objectFit: 'contain',
};

// ── Lightbox ──────────────────────────────────────────────────────────────────

interface GpLightboxProps {
  instances: GpLiteInstance[];
  index: number;
  onClose: () => void;
  onNavigate: (i: number) => void;
}

const GpLightbox: React.FC<GpLightboxProps> = ({ instances, index, onClose, onNavigate }) => {
  const inst = instances[index];
  const caption = captionFor(inst);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onNavigate((index + 1) % instances.length);
      else if (e.key === 'ArrowLeft') onNavigate((index - 1 + instances.length) % instances.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, instances.length, onClose, onNavigate]);

  const navBtn: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(19,27,47,0.85)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: '#fff',
    fontSize: '30px',
    cursor: 'pointer',
    borderRadius: '10px',
    padding: '8px 14px',
    lineHeight: 1,
    transition: 'all 0.15s ease',
    zIndex: 2,
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(2,8,18,0.92)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'relative',
          background: 'rgba(12,26,46,0.97)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '14px',
          padding: '14px 14px 44px',
          maxWidth: '92vw',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '10px',
            right: '12px',
            background: 'rgba(255,255,255,0.06)',
            border: 'none',
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            color: 'rgba(255,255,255,0.8)',
            cursor: 'pointer',
            fontSize: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3,
          }}
        >
          ✕
        </button>

        {caption && (
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', fontWeight: 500, margin: '0 0 10px', alignSelf: 'flex-start' }}>
            {caption}
          </p>
        )}

        {isVideo(inst.mediaUrl) ? (
          <video
            src={inst.mediaUrl}
            controls
            autoPlay
            muted
            loop
            playsInline
            style={{ maxWidth: '84vw', maxHeight: '76vh', borderRadius: '8px', background: '#000', display: 'block' }}
          />
        ) : (
          <img
            src={inst.mediaUrl}
            alt={caption || `Image ${index + 1}`}
            style={{ maxWidth: '84vw', maxHeight: '76vh', borderRadius: '8px', background: '#000', display: 'block' }}
          />
        )}

        {instances.length > 1 && (
          <>
            <button
              style={{ ...navBtn, left: '-70px' }}
              aria-label="Previous"
              onClick={() => onNavigate((index - 1 + instances.length) % instances.length)}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(30,169,234,0.25)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(30,169,234,0.5)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(19,27,47,0.85)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.15)';
              }}
            >
              ‹
            </button>
            <button
              style={{ ...navBtn, right: '-70px' }}
              aria-label="Next"
              onClick={() => onNavigate((index + 1) % instances.length)}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(30,169,234,0.25)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(30,169,234,0.5)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(19,27,47,0.85)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.15)';
              }}
            >
              ›
            </button>
          </>
        )}

        <span
          style={{
            position: 'absolute',
            bottom: '12px',
            fontSize: '12px',
            fontWeight: 600,
            color: 'rgba(255,255,255,0.45)',
            letterSpacing: '0.04em',
          }}
        >
          {index + 1} / {instances.length}
        </span>
      </div>
    </div>
  );
};

/**
 * Lazy media card: nothing is mounted until the card scrolls near the
 * viewport (IntersectionObserver, 200px lookahead). Videos additionally use
 * preload="none" so no bytes are fetched until the GP presses play.
 */
const LazyMedia: React.FC<{ url?: string; alt: string }> = ({ url, alt }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ aspectRatio: '4 / 3', background: '#000' }}>
      {!visible ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: '26px',
              height: '26px',
              border: '2px solid rgba(30,169,234,0.2)',
              borderTopColor: 'rgba(30,169,234,0.7)',
              borderRadius: '50%',
              animation: 'gpSpin 0.8s linear infinite',
            }}
          />
        </div>
      ) : isVideo(url) ? (
        <video
          src={`${url}#t=0.1`}
          controls
          muted
          loop
          playsInline
          preload="metadata"
          style={mediaStyle}
        />
      ) : (
        <img src={url} alt={alt} loading="lazy" style={mediaStyle} />
      )}
    </div>
  );
};

const GpReportView: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();

  const [report, setReport] = useState<GpReportDetail | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [instances, setInstances] = useState<GpLiteInstance[]>([]);
  const [measurements, setMeasurements] = useState<GpMeasurement[]>([]);
  const [tab, setTab] = useState<'report' | 'measurements' | 'images'>('report');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!reportId) return;
    let revoked: string | null = null;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const detail = await gpGetReport(reportId);
        setReport(detail);
        const url = await gpGetReportPdfBlobUrl(reportId);
        revoked = url;
        setPdfUrl(url);
        gpGetReportLiteImages(reportId)
          .then(res => setInstances(res.instances || []))
          .catch(() => setInstances([]));
        gpGetReportMeasurements(reportId)
          .then(res => setMeasurements(res.measurements || []))
          .catch(() => setMeasurements([]));
      } catch (err: any) {
        setError(err?.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [reportId]);

  const reportedBy = displayable(report?.finalizedBy);

  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '9px 20px',
    borderRadius: '8px',
    fontSize: '13.5px',
    fontWeight: 600,
    cursor: 'pointer',
    border: active ? '1px solid rgba(30,169,234,0.5)' : '1px solid rgba(255,255,255,0.12)',
    background: active ? 'rgba(30,169,234,0.15)' : 'transparent',
    color: active ? '#4fc3f7' : 'rgba(255,255,255,0.6)',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
  });

  return (
    <section className="layoutContainer" style={customStyles.layoutBackground}>
      <style>{'@keyframes gpSpin { to { transform: rotate(360deg); } }'}</style>
      <div className="page" style={{ width: '100%' }}>
        <GpHeader />
        <div className="page__body">
          <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
            {/* Back button */}
            <button
              type="button"
              onClick={() => navigate('/gp/reports')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.8)',
                cursor: 'pointer',
                marginBottom: '16px',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(30,169,234,0.12)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(30,169,234,0.4)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.05)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.12)';
              }}
            >
              <span style={{ fontSize: '15px', lineHeight: 1 }}>←</span> Back to Reports
            </button>

            {error && (
              <div
                style={{
                  background: 'rgba(248,113,113,0.08)',
                  border: '1px solid rgba(248,113,113,0.3)',
                  borderRadius: '10px',
                  padding: '14px 18px',
                  fontSize: '13.5px',
                  color: '#f87171',
                  marginBottom: '16px',
                }}
              >
                {error}
              </div>
            )}

            {/* Patient info card */}
            {report && (
              <div
                style={{
                  background: 'rgba(19,27,47,0.72)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                  padding: '18px 22px',
                  marginBottom: '16px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '16px 40px',
                }}
              >
                {/* Patient identity block */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: '220px' }}>
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(30,169,234,0.25) 0%, rgba(30,169,234,0.08) 100%)',
                      border: '1px solid rgba(30,169,234,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      flexShrink: 0,
                    }}
                  >
                    🫀
                  </div>
                  <div>
                    <div style={{ ...infoValueStyle, fontSize: '16px', fontWeight: 700 }}>
                      {report.patientName || '—'}
                    </div>
                    <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.5)' }}>
                      DOB {report.dob || '—'}
                    </div>
                  </div>
                </div>

                <div>
                  <div style={infoLabelStyle}>Study</div>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '3px 12px',
                      borderRadius: '999px',
                      background: report.studyType === 'stressEcho' ? 'rgba(250,204,21,0.12)' : 'rgba(30,169,234,0.12)',
                      color: report.studyType === 'stressEcho' ? 'rgba(250,204,21,0.9)' : '#4fc3f7',
                      border: `1px solid ${report.studyType === 'stressEcho' ? 'rgba(250,204,21,0.3)' : 'rgba(30,169,234,0.3)'}`,
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                    }}
                  >
                    {typeLabel(report.studyType)}
                  </span>
                </div>

                <div>
                  <div style={infoLabelStyle}>Study Date</div>
                  <div style={infoValueStyle}>{formatStudyDateTime(report.studyDate, report.studyTime)}</div>
                </div>

                <div style={{ maxWidth: '280px' }}>
                  <div style={infoLabelStyle}>Clinic</div>
                  <div style={infoValueStyle}>{report.clinicName || '—'}</div>
                </div>

                {reportedBy && (
                  <div>
                    <div style={infoLabelStyle}>Reported By</div>
                    <div style={infoValueStyle}>{reportedBy}</div>
                  </div>
                )}
              </div>
            )}

            {/* Tabs + actions */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
              <button type="button" style={tabBtn(tab === 'report')} onClick={() => setTab('report')}>
                Report
              </button>
              {measurements.length > 0 && (
                <button type="button" style={tabBtn(tab === 'measurements')} onClick={() => setTab('measurements')}>
                  Measurements
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '1px 8px',
                      borderRadius: '999px',
                      background: tab === 'measurements' ? 'rgba(30,169,234,0.25)' : 'rgba(255,255,255,0.1)',
                      color: tab === 'measurements' ? '#4fc3f7' : 'rgba(255,255,255,0.55)',
                    }}
                  >
                    {measurements.length}
                  </span>
                </button>
              )}
              <button type="button" style={tabBtn(tab === 'images')} onClick={() => setTab('images')}>
                Images
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '1px 8px',
                    borderRadius: '999px',
                    background: tab === 'images' ? 'rgba(30,169,234,0.25)' : 'rgba(255,255,255,0.1)',
                    color: tab === 'images' ? '#4fc3f7' : 'rgba(255,255,255,0.55)',
                  }}
                >
                  {instances.length}
                </span>
              </button>
              {pdfUrl && (
                <a
                  className="btn btn__primary"
                  href={pdfUrl}
                  download={`${report?.reportName || 'report'}.pdf`}
                  style={{ marginLeft: 'auto', textDecoration: 'none', borderRadius: '8px', fontSize: '13.5px' }}
                >
                  ⬇ Download PDF
                </a>
              )}
            </div>

            {/* Loading skeleton */}
            {loading && (
              <div
                style={{
                  background: 'rgba(19,27,47,0.72)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  height: '60vh',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '14px',
                }}
              >
                <div
                  style={{
                    width: '42px',
                    height: '42px',
                    border: '3px solid rgba(30,169,234,0.2)',
                    borderTopColor: '#1ea9ea',
                    borderRadius: '50%',
                    animation: 'gpSpin 0.8s linear infinite',
                  }}
                />
                <span style={{ fontSize: '13.5px', color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                  Loading report…
                </span>
              </div>
            )}

            {/* Report tab */}
            {!loading && tab === 'report' && pdfUrl && (
              <div
                style={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                }}
              >
                <iframe
                  title="Report PDF"
                  src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                  style={{
                    width: '100%',
                    height: '78vh',
                    border: 'none',
                    display: 'block',
                    background: '#fff',
                  }}
                />
              </div>
            )}

            {/* Measurements tab */}
            {!loading && tab === 'measurements' && <GpMeasurements measurements={measurements} />}

            {/* Images tab */}
            {!loading && tab === 'images' && (
              instances.length === 0 ? (
                <div
                  style={{
                    background: 'rgba(19,27,47,0.72)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '12px',
                    padding: '60px 20px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(255,255,255,0.55)', marginBottom: '4px' }}>
                    No images available
                  </div>
                  <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.3)' }}>
                    Images for this study have not been published yet
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '14px',
                  }}
                >
                  {instances.map((inst, idx) => {
                    const caption = captionFor(inst);
                    return (
                      <figure
                        key={inst.instanceUID || idx}
                        title="Click to expand"
                        style={{
                          margin: 0,
                          border: '1px solid rgba(255,255,255,0.08)',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          background: '#000',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                          transition: 'transform 0.15s ease, border-color 0.15s ease',
                          cursor: 'pointer',
                        }}
                        onClick={() => setLightboxIndex(idx)}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(30,169,234,0.45)';
                          (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)';
                          (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                        }}
                      >
                        <LazyMedia url={inst.mediaUrl} alt={caption || `Image ${idx + 1}`} />
                        {caption && (
                          <figcaption
                            style={{
                              padding: '8px 12px',
                              fontSize: '12px',
                              fontWeight: 500,
                              color: 'rgba(255,255,255,0.75)',
                              background: 'rgba(19,27,47,0.9)',
                              borderTop: '1px solid rgba(255,255,255,0.06)',
                            }}
                          >
                            {caption}
                          </figcaption>
                        )}
                      </figure>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {lightboxIndex !== null && instances[lightboxIndex] && (
        <GpLightbox
          instances={instances}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </section>
  );
};

export default GpReportView;
